/**
 * AmbassadorLogin - Peace of Sleep Ambassador Login Page
 *
 * Branded login for POS brand ambassadors.
 * Redirects to ambassador dashboard after login.
 *
 * URL: /ambassador/login
 */

import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../pos/pos-theme.css';
import '../pos/pages/pos-login.css';

/** Shape returned from the login user check query */
interface AmbassadorUserCheck {
  is_ambassador: boolean | null;
  permissions: {
    ambassador_portal?: { access?: boolean; level?: string };
    [key: string]: unknown;
  } | null;
}

function AmbassadorLogin() {
  const { supabase } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const prefillEmail = searchParams.get('email') || '';

  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Keep the field in sync if the URL email arrives after first render.
  useEffect(() => {
    if (prefillEmail) setEmail(prefillEmail);
  }, [prefillEmail]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (!supabase) return;
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password
      });

      if (signInError) {
        const msg = signInError.message?.toLowerCase() || '';
        if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('load failed')) {
          setError('Unable to connect — please check your internet connection or try mobile data.');
        } else {
          setError('Invalid email or password');
        }
        setLoading(false);
        return;
      }

      // Check if user has ambassador access
      const { data: rawUserData, error: userError } = await supabase!
        .from('users')
        .select('is_ambassador, permissions')
        .eq('id', data.user.id)
        .single();

      const userData = rawUserData as AmbassadorUserCheck | null;

      // Note: failures below do NOT call supabase.auth.signOut(). A destructive
      // sign-out here would also evict the user's session from any other tab
      // they had open against the admin or operator portals (the Charlie
      // incident, 2026-05-13). We refuse the route quietly and let the user
      // navigate elsewhere with their session still intact.
      if (userError || !userData) {
        setError('Your account is signed in, but we could not load your profile. Please try again or contact CleanMyBed.');
        setLoading(false);
        return;
      }

      // Verify ambassador access — check .level (standard) or .access (legacy)
      const ambPerms = userData.permissions?.ambassador_portal;
      const hasAmbassadorAccess = userData.is_ambassador === true ||
        (ambPerms?.level != null && ambPerms.level !== 'off') ||
        ambPerms?.access === true;

      if (!hasAmbassadorAccess) {
        setError('You’re signed in, but this email is not set up as a Brand Ambassador. If you also use the Operations portal, switch to that tab; otherwise contact CleanMyBed for an invitation.');
        setLoading(false);
        return;
      }

      // Redirect to intended destination or ambassador dashboard
      if (redirectTo) {
        navigate(redirectTo);
      } else {
        navigate('/ambassador');
      }
    } catch (err: unknown) {
      console.error('Login error:', err);
      const msg = (err instanceof Error ? err.message : '').toLowerCase();
      if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('load failed')) {
        setError('Unable to connect — please check your internet connection or try mobile data.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setLoading(false);
    }
  }

  return (
    <div className="pos-login">
      <div className="pos-login-content">
        {/* Brand Header */}
        <div className="pos-login-header">
          <h1 className="pos-login-brand">PEACE OF SLEEP</h1>
          <p className="pos-login-tagline">The Quiet Standard Of Trust</p>
        </div>

        {/* Login Card */}
        <div className="pos-login-card">
          <h2>Welcome Back</h2>
          <p>Sign in to your ambassador dashboard.</p>

          {error && (
            <div className="pos-login-error">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="pos-login-form">
            <div className="pos-login-field">
              <label className="pos-login-label">Email</label>
              <input
                type="email"
                className="pos-login-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="pos-login-field">
              <label className="pos-login-label">Password</label>
              <input
                type="password"
                className="pos-login-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              className="pos-login-btn"
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <div className="text-center mt-lg">
            <Link
              to="/forgot-password"
              style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light, #777)', textDecoration: 'underline' }}
            >
              Forgot your password?
            </Link>
          </div>

          <div className="pos-login-footer">
            <p>
              Don&apos;t have access?
              <br />
              Contact CleanMyBed for an invitation.
            </p>
          </div>
        </div>

        {/* Powered By */}
        <div className="pos-login-powered-by">
          <span>Powered by</span>
          <span className="pos-login-powered-by-brand">CleanMyBed</span>
        </div>
      </div>
    </div>
  );
}

export default AmbassadorLogin;
